import { getTranslatedText, type AssetMedia } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { type ImageProps } from 'expo-image'
import { useState } from 'react'
import { Pressable, View, type StyleProp, type ViewStyle } from 'react-native'
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated'
import CloseButton from '~/components/CloseButton.tsx'
import { FileUpload } from '~/components/FileUpload.tsx'
import { useAlert } from '~/components/GlobalAlert.tsx'
import Icon from '~/components/Icon.tsx'
import Image from '~/components/layout/Image.tsx'
import { Button, ButtonLabel } from '~/components/ui/button.tsx'
import { Text } from '~/components/ui/text.tsx'
import { DEFAULT_LANGUAGE } from '~/data/utils/translatedData.ts'
import { cn } from '~/lib/utils.ts'

export function PropertyMedia({
  media,
  className,
  onMediaUpdate,
}: {
  media: AssetMedia[]
  className?: string
  onMediaUpdate: (newMedia: AssetMedia[]) => void
}) {
  const { t } = useTranslations(['property', 'common'])
  const { showAlert } = useAlert()

  const [addImageActive, setAddImageActive] = useState(false)
  const [selectImageActive, setSelectImageActive] = useState(false)

  const [imageWidth, setImageWidth] = useState(0)

  const handleMediaDelete = async (urlToDelete: string, index: number) => {
    if (!media?.length) {
      return
    }

    try {
      const mediaItem = media.find((m) => m.url === urlToDelete) || media[index]

      if (!mediaItem) {
        return
      }

      const updatedMedia = media.filter((media) => media.url !== mediaItem.url)

      // Re-order remaining items
      const reorderedMedia = updatedMedia
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item, index) => ({ ...item, order: index }))

      return onMediaUpdate?.(reorderedMedia)
    } catch (error) {
      console.error('Error deleting media:', error)

      showAlert({
        type: 'error',
        title: t(($) => $.errors.error),
        description: t(($) => $.errors.mediaDeleteFailed),
      })
    }
  }

  const handleSetAsFirst = async (urlOfItemToPromote: string) => {
    if (!media?.length) {
      return
    }

    const itemToPromote = media.find((item) => item.url === urlOfItemToPromote)

    if (!itemToPromote) {
      return
    }

    const otherItems = media.filter((item) => item.url !== urlOfItemToPromote)

    // Ensure otherItems are sorted by their original order to maintain relative positions
    otherItems.sort((a, b) => (a.order || 0) - (b.order || 0))

    const updatedMedia = [
      { ...itemToPromote, order: 0 },
      ...otherItems.map((item, index) => ({ ...item, order: index + 1 })),
    ]

    onMediaUpdate(updatedMedia)
  }

  const sortedMedia = media ? [...media].sort((a, b) => (a.order || 0) - (b.order || 0)) : []

  const selectedImage = sortedMedia.find((mediaItem) => mediaItem.order === 0)

  return (
    <View className={cn('relative w-full flex-1 gap-4', className)}>
      {media.length > 0 && (
        <View
          className="w-full flex-1"
          onLayout={(e) => {
            setImageWidth(e.nativeEvent.layout.width / 2)
          }}>
          <Animated.View
            layout={LinearTransition.duration(150)}
            className={cn('flex-row flex-wrap gap-4')}>
            {(selectImageActive || !selectedImage) && (
              <>
                {sortedMedia.map((mediaItem, index) => (
                  <MediaItem
                    key={mediaItem.url + mediaItem.storageId + mediaItem.order}
                    mediaItem={mediaItem}
                    onSelect={() => handleSetAsFirst(mediaItem.url)}
                    onDelete={() => handleMediaDelete(mediaItem.url, index)}
                    isSelected={selectedImage?.url === mediaItem.url}
                    style={{
                      width: imageWidth - 10,
                    }}
                    imageStyle={{
                      width: imageWidth - 14,
                    }}
                  />
                ))}
              </>
            )}
            {!selectImageActive && selectedImage && (
              <MediaItem
                key="selected-image"
                mediaItem={selectedImage}
                className="web:shadow-md flex-1"
                imageStyle={{
                  width: '100%',
                }}
              />
            )}
            {media.length < 4 && selectImageActive && !addImageActive && (
              <Animated.View
                entering={FadeIn}
                key="add-button"
                style={{
                  width: imageWidth - 7,
                  aspectRatio: 16 / 9,
                  borderRadius: 6,
                }}
                className="relative gap-3 pl-1">
                <Button
                  variant="outline"
                  className="h-full w-full flex-1 flex-row items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary"
                  onPress={() => setAddImageActive(true)}>
                  <Icon name="add-circle-outline" size={24} />
                  <ButtonLabel>{t(($) => $.actions.addImage)}</ButtonLabel>
                </Button>
              </Animated.View>
            )}
          </Animated.View>
        </View>
      )}
      {(media.length === 0 || addImageActive) && (
        <View className="mt-4 flex-1 items-center justify-center gap-2">
          <FileUpload
            bucket="property-media"
            className="w-full items-center"
            order={media.length + 1}
            onUpload={(newMedia) => {
              onMediaUpdate([...media, newMedia])
              setAddImageActive(false)
            }}
            onError={(error) => {
              console.error('Upload error:', error)

              showAlert({
                type: 'error',
                title: t(($) => $.errors.error),
                description: t(($) => $.errors.mediaUploadFailed),
              })
            }}
            maxSizeBytes={15 * 1024 * 1024}
            multiple={false}
          />
          <Button variant="link" size="sm" onPress={() => setAddImageActive(false)}>
            {t(($) => $.common.navigation.previous)}
          </Button>
        </View>
      )}
      {!addImageActive && (
        <Button
          variant="link"
          size="sm"
          onPress={() => setSelectImageActive((current) => !current)}>
          {!selectImageActive ? t(($) => $.actions.selectAnotherImage) : t(($) => $.actions.done)}
        </Button>
      )}
    </View>
  )
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

function MediaItem({
  mediaItem,
  onSelect,
  onDelete,
  className,
  style,
  imageStyle,
  isSelected,
}: {
  mediaItem: AssetMedia
  onSelect?: () => void
  onDelete?: () => void
  className?: string
  style?: StyleProp<ViewStyle>
  imageStyle?: ImageProps['style']
  isSelected?: boolean
}) {
  return (
    <AnimatedPressable
      onPress={() => onSelect?.()}
      className={cn(
        'relative gap-3 self-start rounded-lg border-2 border-transparent',
        isSelected && 'border-accent',
        className
      )}
      style={style}>
      {mediaItem.type === 'image' && (
        <Image
          source={{ uri: mediaItem.url }}
          errorClassName="p-0"
          style={[
            {
              aspectRatio: 16 / 9,
              borderRadius: 6,
            },
            imageStyle,
          ]}
          contentFit="cover"
        />
      )}
      {!!mediaItem.description && (
        <View className="flex-1">
          <Text className="text-sm text-foreground">
            {getTranslatedText(mediaItem.description, DEFAULT_LANGUAGE)}
          </Text>
        </View>
      )}
      {!!onDelete && (
        <CloseButton
          onPress={() => {
            onDelete()
            return false
          }}
          className="absolute right-1.5 top-2 size-8 border-0 bg-background"
          iconClassName="text-pink-500"
        />
      )}
    </AnimatedPressable>
  )
}
